import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import createApiClient from "../../utils/createApiClient";

export interface LeadData {
  fullname: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  teamSize: string;
  description: string;
  status?: string;
}

export interface Lead {
  _id: string;
  fullname: string;
  email: string;
  companyName: string;
  phoneNumber: string;
  teamSize: string;
  description: string;
  userId: string;
  status?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LeadApiResponse {
  success: boolean;
  message: string;
  data?: Lead;
}

export interface LeadsApiResponse {
  success: boolean;
  message: string;
  data?: {
    leads: Lead[];
    pagination: {
      currentPage: number;
      pageSize: number;
      totalLeads: number;
      totalPages: number;
    };
  };
}

export interface LeadStats {
  totalLeads: number;
  newLeads: number;
  conversionRate: string;
  churnRate: string;
  followUps: number;
}

export interface LeadStatsApiResponse {
  success: boolean;
  message: string;
  data?: LeadStats;
}

export interface MonthlyStat {
  month: string;
  totalLeads: number;
  totalConversions: number;
}

export interface LeadsConversionApiResponse {
  success: boolean;
  message: string;
  data?: {
    year: number;
    stats: MonthlyStat[];
  };
}

function isAxiosError(error: unknown): error is import("axios").AxiosError {
  return (error as import("axios").AxiosError).isAxiosError !== undefined;
}

// CREATE LEAD
export const createLead = createAsyncThunk<
  LeadApiResponse,
  LeadData,
  { rejectValue: string }
>("lead/createLead", async (formData, { rejectWithValue }) => {
  try {
    const response = await createApiClient.post<LeadApiResponse>("/lead", {
      fullname: formData.fullname,
      email: formData.email,
      companyName: formData.companyName,
      phoneNumber: formData.phoneNumber,
      teamSize: formData.teamSize,
      description: formData.description,
      status: "new_lead",
    });

    if (response.data.success) {
      toast.success("Thank you for reaching out. We’ll contact you soon.");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to create lead");
      return rejectWithValue(response.data.message || "Failed to create lead");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET ALL LEADS
export const getAllLeads = createAsyncThunk<
  LeadsApiResponse,
  { page?: number; pageSize?: number },
  { rejectValue: string }
>(
  "lead/getAllLeads",
  async ({ page = 1, pageSize = 5 }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.get<LeadsApiResponse>(
        `/lead?page=${page}&pageSize=${pageSize}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve leads");
        return rejectWithValue(
          response.data.message || "Failed to retrieve leads"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as LeadsApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET PIE CHART LEADS
export const getPieChartLeads = createAsyncThunk<
  LeadsApiResponse,
  { page?: number; pageSize?: number },
  { rejectValue: string }
>(
  "lead/getPieChartLeads",
  async ({ page = 1, pageSize = 10 }, { rejectWithValue }) => {
    try {
      const response = await createApiClient.get<LeadsApiResponse>(
        `/lead?page=${page}&pageSize=${pageSize}`
      );

      if (response.data.success) {
        return response.data;
      } else {
        toast.error(response.data.message || "Failed to retrieve leads");
        return rejectWithValue(
          response.data.message || "Failed to retrieve leads"
        );
      }
    } catch (error) {
      let errorMessage = "An unknown error occurred";
      if (isAxiosError(error) && error.response) {
        errorMessage =
          (error.response.data as LeadsApiResponse).message || "Unknown error";
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

// GET LEAD BY ID
export const getLeadById = createAsyncThunk<
  LeadApiResponse,
  string,
  { rejectValue: string }
>("lead/getLeadById", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<LeadApiResponse>(`/lead/${id}`);

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to retrieve lead");
      return rejectWithValue(
        response.data.message || "Failed to retrieve lead"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE LEAD
export const updateLead = createAsyncThunk<
  LeadApiResponse,
  { id: string; data: Partial<LeadData> },
  { rejectValue: string }
>("lead/updateLead", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await createApiClient.put<LeadApiResponse>(
      `/lead/${id}`,
      data
    );

    if (response.data.success) {
      toast.success(response.data.message || "Lead updated successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to update lead");
      return rejectWithValue(response.data.message || "Failed to update lead");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// UPDATE LEAD STATUS
export const updateLeadStatus = createAsyncThunk<
  LeadApiResponse,
  { id: string; status: string },
  { rejectValue: string }
>("lead/updateLeadStatus", async ({ id, status }, { rejectWithValue }) => {
  try {
    const response = await createApiClient.put<LeadApiResponse>(
      `/lead/${id}/status`,
      { status }
    );

    if (response.data.success) {
      toast.success("Status updated successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to update lead status");
      return rejectWithValue(
        response.data.message || "Failed to update lead status"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// DELETE LEAD
export const deleteLead = createAsyncThunk<
  LeadApiResponse,
  string,
  { rejectValue: string }
>("lead/deleteLead", async (id, { rejectWithValue }) => {
  try {
    const response = await createApiClient.delete<LeadApiResponse>(
      `/lead/${id}`
    );

    if (response.data.success) {
      toast.success(response.data.message || "Lead deleted successfully");
      return response.data;
    } else {
      toast.error(response.data.message || "Failed to delete lead");
      return rejectWithValue(response.data.message || "Failed to delete lead");
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadApiResponse).message || "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET LEAD STATISTICS
export const getLeadStats = createAsyncThunk<
  LeadStatsApiResponse,
  void,
  { rejectValue: string }
>("lead/getLeadStats", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<LeadStatsApiResponse>(
      "/lead/stats"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve lead statistics"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve lead statistics"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadStatsApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});

// GET LEADS CONVERSION
export const getLeadsConversion = createAsyncThunk<
  LeadsConversionApiResponse,
  void,
  { rejectValue: string }
>("lead/getLeadsConversion", async (_, { rejectWithValue }) => {
  try {
    const response = await createApiClient.get<LeadsConversionApiResponse>(
      "/lead/conversion"
    );

    if (response.data.success) {
      return response.data;
    } else {
      toast.error(
        response.data.message || "Failed to retrieve leads conversion"
      );
      return rejectWithValue(
        response.data.message || "Failed to retrieve leads conversion"
      );
    }
  } catch (error) {
    let errorMessage = "An unknown error occurred";
    if (isAxiosError(error) && error.response) {
      errorMessage =
        (error.response.data as LeadsConversionApiResponse).message ||
        "Unknown error";
    } else if (error instanceof Error) {
      errorMessage = error.message;
    }
    toast.error(errorMessage);
    return rejectWithValue(errorMessage);
  }
});
